import { type FormEvent, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/cn';
import {
  type AdminOrgMembership,
  type AdminUser,
  useAdminAddMembership,
  useAdminGrantSystemRole,
  useAdminOrgs,
  useAdminRemoveMembership,
  useAdminRevokeSystemRole,
  useAdminUpdateMembership,
  useAdminUpdateUser,
} from '@/lib/admin';

const ORG_ROLES = ['owner', 'admin', 'member', 'viewer'] as const;

// Site-level roles surfaced in the UI. Today only `admin` exists; if
// new ones are seeded later the UI auto-extends because the checkbox
// list iterates this constant.
const SYSTEM_ROLES = ['admin'] as const;

interface Props {
  user: AdminUser;
  isSelf: boolean;
  onClose: () => void;
}

export function EditUserModal({ user, isSelf, onClose }: Props) {
  const update = useAdminUpdateUser();
  const grant = useAdminGrantSystemRole();
  const revoke = useAdminRevokeSystemRole();

  const [email, setEmail] = useState(user.email);
  const [name, setName] = useState(user.name ?? '');
  const [isSuperuser, setIsSuperuser] = useState(user.is_superuser);
  const [isActive, setIsActive] = useState(user.is_active);
  const [isVerified, setIsVerified] = useState(user.is_verified);

  const onSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await update.mutateAsync({
        userId: user.id,
        input: {
          email: email !== user.email ? email.trim() : undefined,
          name: name.trim() !== (user.name ?? '') ? name.trim() : undefined,
          is_superuser:
            isSuperuser !== user.is_superuser ? isSuperuser : undefined,
          is_active: isActive !== user.is_active ? isActive : undefined,
          is_verified:
            isVerified !== user.is_verified ? isVerified : undefined,
        },
      });
      onClose();
    } catch {
      /* surfaced below */
    }
  };

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-stone-900/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 shadow-xl">
        <div className="flex items-center justify-between border-b border-stone-200 dark:border-stone-800 px-5 py-3">
          <h2 className="text-lg font-semibold">
            Edit <span className="font-mono">{user.email}</span>
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-stone-500 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          <form onSubmit={onSave} className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-stone-500 dark:text-stone-400">
              Identity
            </h3>
            <div className="grid gap-3 md:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
                  Email
                </span>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
                  Name
                </span>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="—"
                />
              </label>
            </div>

            <div className="flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={isSuperuser}
                  onChange={(e) => setIsSuperuser(e.target.checked)}
                  className="h-4 w-4"
                />
                <span>Superuser</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="h-4 w-4"
                  disabled={isSelf}
                />
                <span>Active{isSelf && ' (cannot change own)'}</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={isVerified}
                  onChange={(e) => setIsVerified(e.target.checked)}
                  className="h-4 w-4"
                />
                <span>Email verified</span>
              </label>
            </div>

            {update.isError && (
              <p className="text-sm text-red-600">
                {(update.error as Error).message}
              </p>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="ghost" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? 'Saving…' : 'Save identity'}
              </Button>
            </div>
          </form>

          <section className="space-y-2">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-stone-500 dark:text-stone-400">
              System roles
            </h3>
            <p className="text-xs text-stone-500 dark:text-stone-400">
              Site-level roles. Superuser already implies full access — these
              are extra named roles for non-superuser staff.
            </p>
            <div className="flex flex-wrap gap-3">
              {SYSTEM_ROLES.map((role) => {
                const has = user.system_roles.includes(role);
                const busy =
                  (grant.isPending && grant.variables?.userId === user.id && grant.variables?.roleName === role) ||
                  (revoke.isPending && revoke.variables?.userId === user.id && revoke.variables?.roleName === role);
                return (
                  <button
                    key={role}
                    type="button"
                    onClick={() => {
                      if (has) {
                        revoke.mutate({ userId: user.id, roleName: role });
                      } else {
                        grant.mutate({ userId: user.id, roleName: role });
                      }
                    }}
                    disabled={busy}
                    className={cn(
                      'rounded border px-3 py-1.5 text-sm transition-colors',
                      has
                        ? 'border-forge-500 bg-forge-50 dark:bg-forge-900/40 text-forge-700 dark:text-forge-300'
                        : 'border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-800',
                    )}
                  >
                    {has ? `✓ ${role}` : `+ ${role}`}
                  </button>
                );
              })}
            </div>
            {(grant.isError || revoke.isError) && (
              <p className="text-sm text-red-600">
                {((grant.error || revoke.error) as Error).message}
              </p>
            )}
          </section>

          <MembershipsSection user={user} />
        </div>
      </div>
    </div>
  );
}

function MembershipsSection({ user }: { user: AdminUser }) {
  const orgs = useAdminOrgs();
  const add = useAdminAddMembership();
  const update = useAdminUpdateMembership();
  const remove = useAdminRemoveMembership();

  const [newOrgId, setNewOrgId] = useState<string>('');
  const [newRole, setNewRole] = useState<string>('member');

  const inOrgIds = new Set(user.organizations.map((m) => m.organization_id));
  const candidates =
    orgs.data?.filter((o) => !inOrgIds.has(o.id)) ?? [];

  const onAdd = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!newOrgId) return;
    try {
      await add.mutateAsync({
        userId: user.id,
        input: { organization_id: Number(newOrgId), role: newRole },
      });
      setNewOrgId('');
      setNewRole('member');
    } catch {
      /* surfaced below */
    }
  };

  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-stone-500 dark:text-stone-400">
        Organization memberships
      </h3>
      {user.organizations.length === 0 && (
        <p className="text-sm text-stone-500 dark:text-stone-400">
          User isn't a member of any organization.
        </p>
      )}
      {user.organizations.length > 0 && (
        <div className="overflow-hidden rounded-md border border-stone-200 dark:border-stone-800">
          <table className="w-full text-left text-sm">
            <thead className="bg-stone-50 dark:bg-stone-900 text-xs uppercase text-stone-500 dark:text-stone-400">
              <tr>
                <th className="px-3 py-2">Organization</th>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {user.organizations.map((m) => (
                <MembershipRow
                  key={m.organization_id}
                  membership={m}
                  onChangeRole={(role) =>
                    update.mutate({
                      userId: user.id,
                      orgId: m.organization_id,
                      role,
                    })
                  }
                  onRemove={() =>
                    remove.mutate({
                      userId: user.id,
                      orgId: m.organization_id,
                    })
                  }
                  busyChange={
                    update.isPending &&
                    update.variables?.userId === user.id &&
                    update.variables?.orgId === m.organization_id
                  }
                  busyRemove={
                    remove.isPending &&
                    remove.variables?.userId === user.id &&
                    remove.variables?.orgId === m.organization_id
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {candidates.length > 0 && (
        <form
          onSubmit={onAdd}
          className="mt-2 flex flex-wrap items-end gap-2 rounded-md border border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900/50 p-3"
        >
          <label className="block flex-1 min-w-[180px]">
            <span className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
              Add to organization
            </span>
            <select
              value={newOrgId}
              onChange={(e) => setNewOrgId(e.target.value)}
              className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm"
            >
              <option value="">— pick one —</option>
              {candidates.map((o) => (
                <option key={o.id} value={String(o.id)}>
                  {o.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
              Role
            </span>
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              className="block rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm"
            >
              {ORG_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>
          <Button type="submit" disabled={!newOrgId || add.isPending}>
            {add.isPending ? 'Adding…' : 'Add'}
          </Button>
        </form>
      )}

      {(add.isError || update.isError || remove.isError) && (
        <p className="text-sm text-red-600">
          {(
            (add.error || update.error || remove.error) as Error
          )?.message}
        </p>
      )}
    </section>
  );
}

function MembershipRow({
  membership,
  onChangeRole,
  onRemove,
  busyChange,
  busyRemove,
}: {
  membership: AdminOrgMembership;
  onChangeRole: (role: string) => void;
  onRemove: () => void;
  busyChange: boolean;
  busyRemove: boolean;
}) {
  return (
    <tr className="border-t border-stone-100 dark:border-stone-800">
      <td className="px-3 py-2">
        <div className="font-medium">{membership.organization_name}</div>
        <div className="font-mono text-xs text-stone-500 dark:text-stone-400">
          {membership.organization_slug}
        </div>
      </td>
      <td className="px-3 py-2">
        <select
          value={membership.role}
          onChange={(e) => onChangeRole(e.target.value)}
          disabled={busyChange}
          className="rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-2 py-1 text-sm"
        >
          {ORG_ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </td>
      <td className="px-3 py-2 text-right">
        <button
          type="button"
          onClick={() => {
            if (
              confirm(
                `Remove user from ${membership.organization_name}?`,
              )
            ) {
              onRemove();
            }
          }}
          disabled={busyRemove}
          className="text-xs text-stone-400 dark:text-stone-500 hover:text-red-600"
        >
          {busyRemove ? 'Removing…' : 'Remove'}
        </button>
      </td>
    </tr>
  );
}
